import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';

import clsx from 'clsx';

import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrowDownIcon.svg';
import { useRegisterDismissLayer } from '../../../../context/BackLayerStackContext';
import { NewItemId } from '../../../../shared/constants';
import {
  getSelectOptionLabel,
  isSelectOptionEmpty,
} from '../../../../utils/forms/select-placeholder-utils';
import CloseActionButton from '../../ActionButtons/CloseActionButton';
import ErrorTooltipIcon from '../ErrorTooltipIcon';
import type { IAppSelectWrapper } from './AppSelectWrapper';
import {
  MENU_VIEWPORT_MAX_HEIGHT,
  getMenuProps,
  useStyles,
} from './AppSelectWrapper.styles';

const LISTBOX_PADDING = 8;

const containsText = (text: string, searchText: string) =>
  text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

function SelectCloseActionButton({
  onClose,
}: {
  onClose: (e: SyntheticEvent) => void;
}) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex' }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}>
      <CloseActionButton onClick={onClose} />
    </Box>
  );
}

function computeRange(
  optionScrollTop: number,
  viewportHeight: number,
  total: number,
  rowHeight: number,
  overscan: number,
) {
  const st = Math.max(0, optionScrollTop);
  const start = Math.floor(st / rowHeight);
  const end = Math.ceil((st + viewportHeight) / rowHeight);
  return {
    start: Math.max(0, start - overscan),
    end: Math.min(total, end + overscan),
  };
}

function measureListSubheaderOffset(menu: HTMLUListElement) {
  let offset = 0;
  for (let i = 0; i < menu.children.length; i++) {
    const el = menu.children[i];
    if (
      el instanceof HTMLElement &&
      el.classList.contains('MuiListSubheader-root')
    ) {
      offset += el.offsetHeight;
    } else {
      break;
    }
  }
  return offset;
}

function useVirtualizedSelectSlice({
  enabled,
  items,
  listboxHeight,
  menuListRef,
  scrollTop,
  headerOffset,
  activeIndex,
  selectMenuOpen,
  optionRowHeight,
  overscanRows,
  keyboardNavRef,
}: {
  enabled: boolean;
  items: unknown[];
  listboxHeight: number;
  menuListRef: React.RefObject<HTMLUListElement | null>;
  scrollTop: number;
  headerOffset: number;
  activeIndex: number;
  selectMenuOpen: boolean;
  optionRowHeight: number;
  overscanRows: number;
  keyboardNavRef: React.MutableRefObject<boolean>;
}) {
  const [viewportHeight, setViewportHeight] = useState(listboxHeight);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }
    const el = menuListRef.current;
    if (!el) {
      return;
    }
    setViewportHeight(el.clientHeight || listboxHeight);
  }, [enabled, listboxHeight, menuListRef, selectMenuOpen, items.length]);

  const optionScrollTop = scrollTop - headerOffset;

  const { start: rangeStart, end: rangeEndRaw } = useMemo(() => {
    if (!enabled || items.length === 0) {
      return { start: 0, end: 0 };
    }
    return computeRange(
      optionScrollTop,
      viewportHeight,
      items.length,
      optionRowHeight,
      overscanRows,
    );
  }, [
    enabled,
    optionScrollTop,
    viewportHeight,
    items.length,
    optionRowHeight,
    overscanRows,
  ]);

  const shouldAnchorToActive = keyboardNavRef.current;
  const start =
    enabled && shouldAnchorToActive
      ? Math.min(rangeStart, activeIndex)
      : rangeStart;
  const end =
    enabled && shouldAnchorToActive
      ? Math.min(items.length, Math.max(rangeEndRaw, activeIndex + 1))
      : rangeEndRaw;

  const topSpacer = enabled ? start * optionRowHeight : 0;
  const bottomSpacer = enabled ? (items.length - end) * optionRowHeight : 0;

  const scrollActiveIntoView = useCallback(
    (index: number) => {
      const el = menuListRef.current;
      if (!el) {
        return;
      }
      const rowTop = headerOffset + index * optionRowHeight;
      const rowBottom = rowTop + optionRowHeight;
      const st = el.scrollTop;
      const vh = el.clientHeight;
      const pad = LISTBOX_PADDING;
      if (rowTop < st + pad) {
        el.scrollTop = Math.max(0, rowTop - pad);
      } else if (rowBottom > st + vh - pad) {
        el.scrollTop = rowBottom - vh + pad;
      }
    },
    [menuListRef, headerOffset, optionRowHeight],
  );

  useLayoutEffect(() => {
    if (!enabled || !selectMenuOpen || items.length === 0) {
      return;
    }
    if (!keyboardNavRef.current) {
      return;
    }
    keyboardNavRef.current = false;
    scrollActiveIntoView(activeIndex);
  }, [
    enabled,
    activeIndex,
    selectMenuOpen,
    items.length,
    scrollActiveIntoView,
    keyboardNavRef,
  ]);

  return { start, end, topSpacer, bottomSpacer };
}

export interface IAppSelectWrapperVirtualized extends IAppSelectWrapper {
  optionRowHeight: number;
  visibleRowsCap: number;
  overscanRows?: number;
  virtualizeMinOptions?: number;
}

const AppSelectWrapperVirtualized: React.FC<IAppSelectWrapperVirtualized> = ({
  disabled = false,
  name,
  className = '',
  additionalOptions = [],
  options = [],
  label,
  defaultValue,
  explainingText = undefined,
  disableSorting = false,
  disableClear = false,
  fieldName = 'name',
  sortBy = fieldName,
  autoWidth = true,
  error = false,
  renderOption,
  renderValue,
  placeholder,
  onChange = () => {},
  onClearCallback = () => {},
  helperText,
  viewMode = false,
  disableSearch = false,
  mode = 'light',
  optionRowHeight,
  visibleRowsCap,
  overscanRows = 5,
  virtualizeMinOptions = 48,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { classes } = useStyles({ mode });
  const hasLabel = Boolean(label);
  const labelClassName = clsx(
    classes.label,
    explainingText ? classes.labelWithExplaining : null,
  );
  const inputLabelProps = hasLabel
    ? {
        ...(props.InputLabelProps || {}),
        shrink: true,
        className: clsx(props.InputLabelProps?.className, labelClassName),
      }
    : props.InputLabelProps;
  const [searchText, setSearchText] = useState('');
  const [selectMenuOpen, setSelectMenuOpen] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const scrollFrameRef = useRef<number | undefined>(undefined);
  const pendingScrollTopRef = useRef(0);
  const keyboardNavRef = useRef(false);
  const selectMenuOpenPrevRef = useRef(selectMenuOpen);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const closeSelectMenu = useCallback(() => {
    setSelectMenuOpen(false);
    setTimeout(() => (document.activeElement as HTMLElement)?.blur());
  }, []);
  useRegisterDismissLayer(selectMenuOpen, closeSelectMenu);

  const index = useMemo(
    () =>
      [...additionalOptions, ...options]?.findIndex((option) => {
        const optionId = option?.id;

        return optionId === defaultValue || optionId === defaultValue?.id;
      }),
    [options, additionalOptions, defaultValue],
  );

  const sortedOptions = useMemo(() => {
    const mainOptions = !disableSorting
      ? (options ? [...options] : [])?.sort((a, b) =>
          `${a[sortBy]}`.localeCompare(`${b[sortBy]}`),
        )
      : options;
    return [...additionalOptions, ...mainOptions];
  }, [options, additionalOptions, disableSorting, sortBy]);

  const valueOutOfOption = useMemo(() => {
    const hasDefaultValue = index === -1 && defaultValue?.[fieldName];

    if (hasDefaultValue) {
      return {
        [fieldName]: defaultValue[fieldName],
        disabled: true,
      };
    }

    return null;
  }, [index, defaultValue, fieldName]);

  const value = useMemo(
    () =>
      index === -1 || index === undefined
        ? valueOutOfOption || ''
        : [...additionalOptions, ...options][index],
    [index, valueOutOfOption, options, additionalOptions],
  );

  const resolvedRenderValue = useMemo(() => {
    if (renderValue) {
      return renderValue;
    }

    if (!placeholder) {
      return undefined;
    }

    return (selected: unknown) => {
      if (isSelectOptionEmpty(selected, fieldName)) {
        return (
          <Typography variant="text-md" color="text.secondary">
            {placeholder}
          </Typography>
        );
      }

      return getSelectOptionLabel(selected, fieldName);
    };
  }, [renderValue, placeholder, fieldName]);

  const renderItemsSource = useMemo(() => {
    if (valueOutOfOption) {
      return [valueOutOfOption, ...(sortedOptions || [])];
    }
    return sortedOptions || [];
  }, [valueOutOfOption, sortedOptions]);

  const filteredOptions = useMemo(() => {
    return renderItemsSource.filter((item) => {
      if (item?.id === NewItemId) {
        return !item.disabled;
      }
      if (item.disabled) {
        return false;
      }
      return containsText(item[fieldName], searchText);
    });
  }, [renderItemsSource, searchText, fieldName]);

  const shouldVirtualize = filteredOptions.length >= virtualizeMinOptions;

  const listboxHeight = useMemo(() => {
    const count = filteredOptions.length;
    const rows = Math.min(count, visibleRowsCap);
    return rows * optionRowHeight + 2 * LISTBOX_PADDING;
  }, [filteredOptions.length, visibleRowsCap, optionRowHeight]);

  const isOptionSelected = useCallback(
    (item: unknown) => {
      if (item == null || value == null) {
        return false;
      }
      const itemId = (item as { id?: unknown }).id;
      const valueId = (value as { id?: unknown }).id;
      if (itemId !== undefined && valueId !== undefined) {
        return itemId === valueId;
      }
      return item === value;
    },
    [value],
  );

  useEffect(() => {
    if (!selectMenuOpen || !shouldVirtualize) {
      return;
    }
    const ix = filteredOptions.findIndex((o) => isOptionSelected(o));
    setActiveIndex(ix >= 0 ? ix : 0);
  }, [
    selectMenuOpen,
    shouldVirtualize,
    searchText,
    filteredOptions,
    isOptionSelected,
  ]);

  const syncScrollTopFromMenu = useCallback(() => {
    const el = menuListRef.current;
    if (!el) {
      return;
    }
    const next = el.scrollTop;
    pendingScrollTopRef.current = next;
    setScrollTop(next);
  }, []);

  useEffect(() => {
    if (!selectMenuOpen) {
      pendingScrollTopRef.current = 0;
      setScrollTop(0);
    }
  }, [selectMenuOpen]);

  useLayoutEffect(() => {
    if (!selectMenuOpen) {
      selectMenuOpenPrevRef.current = false;
      return;
    }
    if (!shouldVirtualize || !menuListRef.current) {
      return;
    }
    const menu = menuListRef.current;
    const offset = measureListSubheaderOffset(menu);
    setHeaderOffset(offset);
    const wasOpening = !selectMenuOpenPrevRef.current;
    selectMenuOpenPrevRef.current = true;
    syncScrollTopFromMenu();
    if (wasOpening) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          syncScrollTopFromMenu();
        });
      });
    }
  }, [
    selectMenuOpen,
    shouldVirtualize,
    filteredOptions,
    disableSearch,
    isMobile,
    optionRowHeight,
    syncScrollTopFromMenu,
  ]);

  useLayoutEffect(() => {
    if (!selectMenuOpen || !shouldVirtualize || !menuListRef.current) {
      return;
    }
    const menu = menuListRef.current;
    const ro = new ResizeObserver(() => {
      setHeaderOffset(measureListSubheaderOffset(menu));
      syncScrollTopFromMenu();
    });
    ro.observe(menu);
    return () => ro.disconnect();
  }, [
    selectMenuOpen,
    shouldVirtualize,
    disableSearch,
    isMobile,
    syncScrollTopFromMenu,
  ]);

  const handleMenuListScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      const next = e.currentTarget.scrollTop;
      pendingScrollTopRef.current = next;
      if (scrollFrameRef.current != null) {
        return;
      }
      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = undefined;
        setScrollTop(pendingScrollTopRef.current);
      });
    },
    [],
  );

  const handleVirtualListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (
        !shouldVirtualize ||
        !selectMenuOpen ||
        filteredOptions.length === 0
      ) {
        return;
      }
      const { key } = e;
      if (
        key !== 'ArrowDown' &&
        key !== 'ArrowUp' &&
        key !== 'Home' &&
        key !== 'End' &&
        key !== 'Enter' &&
        key !== ' '
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (key === 'ArrowDown') {
        keyboardNavRef.current = true;
        setActiveIndex((i) => Math.min(filteredOptions.length - 1, i + 1));
        return;
      }
      if (key === 'ArrowUp') {
        keyboardNavRef.current = true;
        setActiveIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (key === 'Home') {
        keyboardNavRef.current = true;
        setActiveIndex(0);
        return;
      }
      if (key === 'End') {
        keyboardNavRef.current = true;
        setActiveIndex(filteredOptions.length - 1);
        return;
      }
      if (key === 'Enter' || key === ' ') {
        const item = filteredOptions[activeIndexRef.current];
        if (item) {
          const synthetic = {
            target: { value: item, name },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(synthetic);
          closeSelectMenu();
        }
      }
    },
    [
      shouldVirtualize,
      selectMenuOpen,
      filteredOptions,
      name,
      onChange,
      closeSelectMenu,
    ],
  );

  const menuProps = useMemo(() => {
    const base = getMenuProps(theme);
    const paperSx = {
      ...(base.PaperProps?.sx as Record<string, unknown>),
      [theme.breakpoints.up('md')]: {
        width: ref?.current?.offsetWidth,
        maxWidth: `${ref?.current?.offsetWidth} !important`,
        maxHeight: MENU_VIEWPORT_MAX_HEIGHT,
        overflow: 'hidden',
      },
    };
    const virtualListProps = shouldVirtualize
      ? {
          ref: menuListRef,
          onScroll: handleMenuListScroll,
          sx: {
            maxHeight: `min(${listboxHeight}px, ${MENU_VIEWPORT_MAX_HEIGHT})`,
            overflowY: 'auto',
            padding: 0,
          },
          onKeyDownCapture: handleVirtualListKeyDown,
        }
      : undefined;
    return {
      ...base,
      ...(shouldVirtualize ? { disableAutoFocusItem: true } : {}),
      PaperProps: {
        ...base.PaperProps,
        sx: paperSx,
      },
      ...(virtualListProps && {
        MenuListProps: virtualListProps,
      }),
    };
  }, [
    theme,
    shouldVirtualize,
    listboxHeight,
    handleMenuListScroll,
    handleVirtualListKeyDown,
  ]);

  const virtualizedSlice = useVirtualizedSelectSlice({
    enabled: shouldVirtualize,
    items: filteredOptions,
    listboxHeight,
    menuListRef,
    scrollTop,
    headerOffset,
    activeIndex,
    selectMenuOpen,
    optionRowHeight,
    overscanRows,
    keyboardNavRef,
  });

  if (!(options.length + additionalOptions.length)) {
    return (
      <FormControl className={classes.formControl}>
        <TextField
          fullWidth
          disabled={true}
          label={label}
          InputLabelProps={inputLabelProps}
          InputProps={hasLabel ? { notched: false } : undefined}
          className={clsx(className, classes.root)}
          value={defaultValue?.[fieldName]}
          name={name}
          variant="outlined"
          {...props}
          margin="none"
        />
      </FormControl>
    );
  }

  function renderEndAdornment() {
    const closeIcon = !disabled && defaultValue && !disableClear && (
      <IconButton
        disabled={disabled}
        className={classes.closeIcon}
        aria-label="remove value"
        onClick={() => {
          onChange(null);
          onClearCallback();
        }}
        size="large">
        <CloseIcon />
      </IconButton>
    );

    const errorIcon = error && <ErrorTooltipIcon helperText={helperText} />;

    return (
      <InputAdornment
        position="end"
        style={{ right: 35, position: 'absolute' }}>
        {closeIcon}
        {errorIcon}
      </InputAdornment>
    );
  }

  if (viewMode) {
    return (
      <Box className={classes.viewModeWrapper}>
        {label && <Typography variant="text-md-sb">{label}</Typography>}
        <Typography variant="text-md">{defaultValue?.name}</Typography>
      </Box>
    );
  }

  const explainingAdornment = explainingText ? (
    <InputAdornment position="start">
      <Tooltip title={explainingText} classes={{ popper: classes.toolTip }}>
        <IconButton size="large" className={classes.explainingToolTip}>
          <HelpOutlineIcon />
        </IconButton>
      </Tooltip>
    </InputAdornment>
  ) : null;

  return (
    <FormControl className={classes.formControl}>
      <TextField
        fullWidth
        select
        label={label}
        disabled={disabled}
        id={name}
        name={name}
        ref={ref}
        variant="outlined"
        value={value}
        onChange={(d: React.ChangeEvent<HTMLInputElement>) => {
          onChange(d);
        }}
        error={error}
        className={clsx(className, classes.root)}
        SelectProps={{
          autoWidth,
          open: selectMenuOpen,
          onOpen: () => setSelectMenuOpen(true),
          onClose: closeSelectMenu,
          IconComponent: ArrowDownIcon,
          ...(resolvedRenderValue
            ? {
                displayEmpty: true,
                renderValue: () => resolvedRenderValue(value ?? null),
              }
            : shouldVirtualize
              ? {
                  renderValue: (selected: unknown) => {
                    if (selected == null || selected === '') {
                      return '';
                    }
                    if (
                      renderOption &&
                      typeof selected === 'object' &&
                      selected !== null
                    ) {
                      return renderOption(selected);
                    }
                    if (
                      typeof selected === 'object' &&
                      selected !== null &&
                      fieldName in selected
                    ) {
                      const raw = (selected as Record<string, unknown>)[
                        fieldName
                      ];
                      return raw == null ? '' : String(raw);
                    }
                    return String(selected);
                  },
                }
              : {}),
          MenuProps: menuProps,
          ...(shouldVirtualize && {
            onKeyDown: handleVirtualListKeyDown,
          }),
        }}
        InputLabelProps={inputLabelProps}
        InputProps={{
          ...(hasLabel ? { notched: false } : {}),
          endAdornment: renderEndAdornment(),
          startAdornment: explainingAdornment,
        }}
        {...props}>
        {disableSearch ? (
          <ListSubheader
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'flex-end',
              alignItems: 'center',
              lineHeight: 1,
              p: theme.spacing(1),
            }}>
            <SelectCloseActionButton
              onClose={(e) => {
                e.stopPropagation();
                e.preventDefault();
                closeSelectMenu();
              }}
            />
          </ListSubheader>
        ) : (
          <>
            <ListSubheader
              sx={{
                display: { xs: 'block', md: 'none' },
                lineHeight: 1,
                p: theme.spacing(1),
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}>
                <TextField
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                  className={classes.searchField}
                  autoFocus={isMobile}
                  variant="outlined"
                  placeholder={t('common.typeToSearch')}
                  value={searchText}
                  sx={{ flex: 1, minWidth: 0 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  onFocus={() => {
                    setSearchText('');
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      e.stopPropagation();
                    }
                  }}
                />
                <SelectCloseActionButton
                  onClose={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    closeSelectMenu();
                  }}
                />
              </Box>
            </ListSubheader>
            <ListSubheader
              sx={{
                display: { xs: 'none', md: 'block' },
                p: theme.spacing(1),
              }}>
              <TextField
                onClick={(e) => e.stopPropagation()}
                size="small"
                className={classes.searchField}
                autoFocus={!isMobile}
                variant="outlined"
                placeholder={t('common.typeToSearch')}
                fullWidth
                value={searchText}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onFocus={() => {
                  setSearchText('');
                }}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
          </>
        )}
        {shouldVirtualize
          ? [
              ...(virtualizedSlice.topSpacer > 0
                ? [
                    <Box
                      key="__vtop"
                      component="li"
                      role="presentation"
                      sx={{
                        display: 'block',
                        height: virtualizedSlice.topSpacer,
                        p: 0,
                        m: 0,
                        listStyle: 'none',
                        flexShrink: 0,
                      }}
                    />,
                  ]
                : []),
              ...filteredOptions
                .slice(virtualizedSlice.start, virtualizedSlice.end)
                .map((item, i) => {
                  const globalIndex = virtualizedSlice.start + i;
                  const selected = isOptionSelected(item);
                  const focusHighlight = globalIndex === activeIndex;
                  return (
                    <MenuItem
                      key={`${item.id}${item[fieldName]}${globalIndex}`}
                      value={item}
                      id={item.id}
                      onMouseEnter={() => setActiveIndex(globalIndex)}
                      sx={
                        focusHighlight && !selected
                          ? {
                              backgroundColor: theme.palette.action.hover,
                            }
                          : undefined
                      }>
                      {renderOption ? renderOption(item) : item[fieldName]}
                    </MenuItem>
                  );
                }),
              ...(virtualizedSlice.bottomSpacer > 0
                ? [
                    <Box
                      key="__vbottom"
                      component="li"
                      role="presentation"
                      sx={{
                        display: 'block',
                        height: virtualizedSlice.bottomSpacer,
                        p: 0,
                        m: 0,
                        listStyle: 'none',
                        flexShrink: 0,
                      }}
                    />,
                  ]
                : []),
            ]
          : filteredOptions.map((item, itemIndex) => (
              <MenuItem
                key={`${item.id}${item[fieldName]}${itemIndex}`}
                value={item}
                id={item.id}>
                {renderOption ? renderOption(item) : item[fieldName]}
              </MenuItem>
            ))}
      </TextField>
    </FormControl>
  );
};

export default AppSelectWrapperVirtualized;
